/**
 * CLI Benchmark A/B Experiments
 *
 * Launch and inspect ad-hoc benchmark A/B experiments over benchmark-org
 * workflows. Each experiment splits the selected workflows across 1–4 labeled
 * arms; every arm carries an override bundle (model / knobs / feature flags) so
 * the same command runs model comparisons, cache-flag experiments, toolset
 * variants, etc. Wraps the superadmin `/superadmin/benchmark/*` endpoints.
 */

import process from 'node:process';
import { resolveConfig, hasFlag } from './auth.js';
import { handleAb, handleImport, handleList, handleWorkflows } from './benchmark/commands.js';
import { printBenchmarkHelp } from './benchmark/help.js';
import { handleReport } from './benchmark/report.js';

export { inferKnobValue, parseAbArgs } from './benchmark/args.js';
export type { FlagValue, ParsedAbArgs, ParsedArm, ProviderName } from './benchmark/args.js';

export async function runBenchmark(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printBenchmarkHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'workflows':
      await handleWorkflows(rest, apiUrl, token);
      break;
    case 'ab':
      await handleAb(rest, apiUrl, token);
      break;
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'report':
      await handleReport(rest, apiUrl, token);
      break;
    case 'import':
      await handleImport(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printBenchmarkHelp();
      process.exit(1);
  }
}
